import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Table, Badge, Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([AllCommunityModule]);

/* ── helpers ── */
const fmt      = (n) => n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n));
const fmtD     = (n, d = 2) => n == null ? "—" : Number(n).toFixed(d);
const pnlColor = (v) => (v == null ? "#333" : v >= 0 ? "#28a745" : "#dc3545");
const pnlFmt   = (v) => { if (v == null) return "—"; return `${v >= 0 ? "+" : "-"}₹${fmt(v)}`; };

const SummaryCard = ({ label, value, color }) => (
  <Card className="mb-3" style={{ borderRadius: 10, border: "1px solid #eee" }}>
    <CardBody style={{ padding: "12px 16px" }}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || "#333" }}>{value}</div>
    </CardBody>
  </Card>
);

/* Sort: STOCK → FUTURE → PE → CE */
const sortKey = (r) => r.instrument === "STOCK" ? 0 : r.instrument === "FUTURE" ? 1 : r.optionType === "PE" ? 2 : 3;

const TradeDetail = () => {
  const { stockname }       = useParams();
  const { selectedAccount } = useAccount();

  const [loading, setLoading]         = useState(false);
  const [refreshing, setRefreshing]   = useState(false);
  const [stockInfo, setStockInfo]     = useState(null);
  const [options, setOptions]         = useState([]);
  const [futures, setFutures]         = useState([]);
  const [totals, setTotals]           = useState(null);
  const [rowData, setRowData]         = useState([]);
  const [orderResults, setOrderResults] = useState(null);
  const [submitError, setSubmitError]   = useState(null);
  const [availableExpiries, setAvailableExpiries] = useState([]);

  const [showOptionForm, setShowOptionForm] = useState(false);
  const [optionForm, setOptionForm] = useState({
    type: "CE", expiry: "", lots: 1, side: "SELL",
    contracts: [], loadingContracts: false, contractsError: null,
    selectedStrike: "", selectedContract: null,
  });

  const [showStockForm, setShowStockForm] = useState(false);
  const [stockForm, setStockForm] = useState({ qty: 1, side: "BUY", limitPrice: "" });

  const [showFutureForm, setShowFutureForm] = useState(false);
  const [futureForm, setFutureForm] = useState({
    lots: 1, side: "BUY",
    contracts: [], loadingContracts: false, contractsError: null,
    selectedSymbol: "", selectedContract: null,
  });

  const [showAlertForm, setShowAlertForm] = useState(false);
  const [alertForm, setAlertForm] = useState({ direction: "above", target_price: "", note: "" });
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  const [strategyInfo, setStrategyInfo]   = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignExpiry, setAssignExpiry]   = useState("");
  const [assignSaving, setAssignSaving]   = useState(false);
  const [assignMsg, setAssignMsg]         = useState(null);

  /* ── build + sort rows ── */
  const buildRows = (stock, opts, futs) => {
    // Prefer lot_size from stock data (returned by backend even with no options),
    // fall back to the first option's lotsize, then 1.
    const lotsize = (stock?.lot_size) || (opts.length > 0 ? (opts[0].lotsize || 1) : 1);
    const rows = [];

    if (stock) {
      const stockLots = parseFloat((stock.quantity / lotsize).toFixed(2));
      rows.push({
        id: "STOCK", instrument: "STOCK", optionType: null,
        expiry: null, strike: null,
        side: null,
        qty: stock.quantity,
        lots: stockLots,
        _lotsize: lotsize,
        ltp: stock.ltp, avgPrice: stock.avg_price,
        pnl: stock.stock_pnl, potential_pnl: Math.max(0, stock.stock_pnl || 0),
        netDelta: stockLots,
        iv: null, delta: stockLots, theta: null,
      });
    }

    (futs || []).forEach(fut => {
      const ls = fut.lotsize || 1;
      const lots = parseFloat((fut.netqty / ls).toFixed(2));
      rows.push({
        id: `FUT-${fut.symbolname}`, instrument: "FUTURE", optionType: null,
        expiry: fut.expirydate, strike: null,
        side: null,
        qty: fut.netqty,
        lots,
        _lotsize: ls,
        ltp: fut.ltp, avgPrice: fut.buyprice,
        pnl: fut.unrealised, potential_pnl: fut.unrealised,
        netDelta: fut.delta,
        iv: null, delta: fut.delta, theta: null,
      });
    });

    opts.forEach(opt => {
      const nq = opt.netqty;
      const ls = opt.lotsize || 1;
      // Use totallots from DB (already an integer lot count).
      // Fall back to division only for new/manual rows where totallots isn't set.
      const lots = (opt.totallots != null && opt.totallots > 0)
        ? opt.totallots
        : parseFloat((Math.abs(nq) / ls).toFixed(2));
      rows.push({
        id: `OPT-${opt.id}`, instrument: "OPTION", optionType: opt.optiontype,
        expiry: opt.expirydate, strike: opt.strikeprice,
        side: null,
        qty: nq,
        lots,
        ltp: opt.ltp,
        avgPrice: nq < 0 ? opt.totalsellavgprice : opt.totalbuyavgprice,
        pnl: opt.unrealised, potential_pnl: opt.potential_pnl,
        netDelta: (opt.delta != null && nq != null)
          ? parseFloat((opt.delta * Math.sign(nq)).toFixed(2))
          : null,
        iv: opt.implied_volatility, delta: opt.delta, theta: opt.theta,
        _isNew: false,
      });
    });

    rows.sort((a, b) => sortKey(a) - sortKey(b));
    return rows;
  };

  /* ── fetch expiries ── */
  useEffect(() => {
    axios.get("/expiries", { params: { symbol: stockname } })
      .then(res => {
        const exp = res.data.expiries || [];
        setAvailableExpiries(exp);
        if (exp.length > 0) {
          setOptionForm(f => ({ ...f, expiry: exp[0] }));
          setAssignExpiry(exp[0]);
        }
      })
      .catch(() => setAvailableExpiries([]));
  }, []);

  /* ── check if a PH strategy exists for this symbol ── */
  useEffect(() => {
    if (!selectedAccount || selectedAccount === "All Accounts" || !stockname) return;
    axios.get("/strategy/check", { params: { username: selectedAccount, symbolname: stockname } })
      .then(res => setStrategyInfo(res.data))
      .catch(() => setStrategyInfo({ exists: false }));
  }, [selectedAccount, stockname]);

  /* ── fetch option contracts when type or expiry changes (form open) ── */
  useEffect(() => {
    if (!showOptionForm || !optionForm.expiry) return;
    const ltp = stockInfo?.ltp;
    if (!ltp) return;
    setOptionForm(f => ({ ...f, loadingContracts: true, contractsError: null, contracts: [], selectedStrike: "", selectedContract: null }));
    axios.get("/options/snapshot", {
      params: {
        username: selectedAccount,
        stock_name: stockname,
        expiry: optionForm.expiry,
        ce_pe: optionForm.type,
        underlying_ltp: ltp,
      }
    })
      .then(res => {
        const contracts = res.data.contracts || [];
        setOptionForm(f => ({ ...f, contracts, loadingContracts: false }));
      })
      .catch(err => {
        setOptionForm(f => ({ ...f, loadingContracts: false, contractsError: err.response?.data?.error || "Failed to load strikes" }));
      });
  }, [showOptionForm, optionForm.type, optionForm.expiry, stockname, selectedAccount, stockInfo?.ltp]);

  /* ── fetch future contracts when form opens ── */
  useEffect(() => {
    if (!showFutureForm) return;
    setFutureForm(f => ({ ...f, loadingContracts: true, contractsError: null, contracts: [], selectedSymbol: "", selectedContract: null }));
    axios.get("/futures/snapshot", {
      params: { username: selectedAccount, stock_name: stockname }
    })
      .then(res => {
        const contracts = res.data.contracts || [];
        setFutureForm(f => ({ ...f, contracts, loadingContracts: false }));
      })
      .catch(err => {
        setFutureForm(f => ({ ...f, loadingContracts: false, contractsError: err.response?.data?.error || "Failed to load future contracts" }));
      });
  }, [showFutureForm, stockname, selectedAccount]);

  /* ── fetch ── */
  const loadData = useCallback(() => {
    if (!selectedAccount || selectedAccount === "All Accounts") return;
    setLoading(true);
    axios.get("/positions/stock-detail", {
      params: { username: selectedAccount, stock_name: stockname }
    })
      .then(r => {
        const { stock, options: opts, futures: futs, totals: t } = r.data;
        setStockInfo(stock);
        setOptions(opts || []);
        setFutures(futs || []);
        setTotals(t);
        setRowData(buildRows(stock, opts || [], futs || []));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [stockname, selectedAccount]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!selectedAccount || selectedAccount === "All Accounts" || refreshing) return;
    setRefreshing(true);
    axios.post("/positions/sync-symbol", { username: selectedAccount, symbol: stockname })
      .then(() => loadData())
      .catch(console.error)
      .finally(() => setRefreshing(false));
  }, [selectedAccount, stockname, refreshing, loadData]);

  /* ── pinned totals row ── */
  const pinnedRow = useMemo(() => {
    if (!totals) return [];
    const sumDelta = rowData.reduce((s, r) => s + (r.netDelta || 0), 0);
    return [{
      id: "TOTAL", instrument: "TOTAL",
      pnl: totals.combined_pnl,
      potential_pnl: totals.potential_pnl,
      netDelta: parseFloat(sumDelta.toFixed(2)),
    }];
  }, [totals, rowData]);

  /* ── column defs ── */
  const columnDefs = useMemo(() => [
    {
      headerName: "Type", minWidth: 80, maxWidth: 110,
      valueGetter: p => {
        if (p.node.rowPinned) return "TOTAL";
        const d = p.data;
        if (d.instrument === "STOCK") return "STOCK";
        if (d.instrument === "FUTURE") return "FUTURE";
        return d.optionType === "CE" ? "CALL" : "PUT";
      },
      editable: p => !p.node.rowPinned && p.data?._isNew && p.data?.instrument === "OPTION",
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: ["CALL", "PUT"] },
      valueSetter: p => {
        p.data.optionType = p.newValue === "CALL" ? "CE" : "PE";
        return true;
      },
      cellStyle: p => {
        const v = p.value;
        if (v === "TOTAL") return { fontWeight: 700 };
        const color = v === "STOCK" ? "#6f42c1" : v === "FUTURE" ? "#20c997" : v === "CALL" ? "#17a2b8" : "#fd7e14";
        return { color, fontWeight: 600 };
      },
    },
    {
      headerName: "Strike / Qty", minWidth: 100,
      valueGetter: p => {
        if (p.node.rowPinned) return "";
        const d = p.data;
        if (d.instrument === "STOCK" || d.instrument === "FUTURE") return d.qty != null ? Number(d.qty).toLocaleString() : "—";
        return d.strike ? `₹${d.strike}` : (d._isNew ? "0" : "—");
      },
      editable: p => !p.node.rowPinned && p.data?._isNew,
      valueSetter: p => {
        const val = Number(p.newValue) || 0;
        if (p.data.instrument === "OPTION") {
          p.data.strike = val;
        } else {
          // For stock rows, qty and lots are the same (lotsize = 1)
          p.data.qty  = val;
          p.data.lots = val;
        }
        return true;
      },
    },
    {
      headerName: "Expiry", minWidth: 130,
      valueGetter: p => p.node.rowPinned ? "" : (p.data.expiry || "—"),
      editable: p => !p.node.rowPinned && p.data?._isNew && p.data?.instrument === "OPTION",
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: availableExpiries.length > 0 ? availableExpiries : ["—"] },
      valueSetter: p => {
        p.data.expiry = p.newValue;
        return true;
      },
    },
    {
      headerName: "Qty / Lots", minWidth: 80, maxWidth: 110,
      editable: p => !p.node.rowPinned,
      valueGetter: p => {
        if (p.node.rowPinned) return "";
        // Stocks: show raw qty so user knows they're trading shares, not lots
        if (p.data.instrument === "STOCK") return p.data.qty != null ? p.data.qty : "—";
        return p.data.lots != null ? p.data.lots : "—";
      },
      valueSetter: p => {
        const entered = parseFloat(p.newValue) || 0;
        if (p.data.instrument === "STOCK") {
          p.data.qty  = Math.round(entered);   // user enters shares directly
          p.data.lots = p.data._lotsize > 1 ? parseFloat((entered / p.data._lotsize).toFixed(2)) : entered;
        } else {
          p.data.lots = entered;
          p.data.qty  = Math.round(entered * (p.data._lotsize || 1));
        }
        return true;
      },
      cellRenderer: p => {
        if (p.node.rowPinned) return "";
        const d = p.data;
        if (d.instrument === "STOCK") {
          const qtyStr = d.qty != null ? d.qty.toLocaleString() : "—";
          if (d._lotsize > 1 && d.qty) {
            const lotsStr = parseFloat((d.qty / d._lotsize).toFixed(2));
            return `${qtyStr} <span style="color:#888;font-size:11px">(${lotsStr}L)</span>`;
          }
          return qtyStr;
        }
        return d.lots != null ? d.lots : "—";
      },
      cellStyle: () => ({}),
      tooltipValueGetter: p => {
        if (!p.data || p.node.rowPinned) return "";
        if (p.data.instrument === "STOCK")
          return `Enter number of shares to trade${p.data._lotsize > 1 ? ` (lot size: ${p.data._lotsize})` : ""}`;
        if (p.data.instrument === "FUTURE")
          return `Enter number of lots (lot size: ${p.data._lotsize})`;
        return "Enter number of lots";
      },
    },
    {
      headerName: "Action", field: "side", minWidth: 120,
      editable: p => !p.node.rowPinned,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: ["BUY", "SELL"] },
      valueGetter: p => {
        if (p.node.rowPinned) return "";
        return p.data.side || "";
      },
      cellStyle: p => {
        if (p.node.rowPinned) return {};
        const v = p.data.side;
        if (!v) return { color: "#bbb", fontStyle: "italic" };
        return { color: v === "BUY" ? "#28a745" : "#dc3545", fontWeight: 700 };
      },
      cellRenderer: p => {
        if (p.node.rowPinned) return "";
        const v = p.data.side;
        return v ? v : "— select —";
      },
    },
    {
      headerName: "Limit ₹", field: "limitPrice", minWidth: 90, maxWidth: 110,
      editable: p => !p.node.rowPinned && !!p.data.side,
      valueGetter: p => p.node.rowPinned ? "" : (p.data.limitPrice || ""),
      valueSetter: p => {
        p.data.limitPrice = p.newValue ? Number(p.newValue) : null;
        return true;
      },
      cellStyle: p => {
        if (p.node.rowPinned) return {};
        if (p.data.limitPrice) return { color: "#0055aa", fontWeight: 600 };
        return { color: "#bbb", fontStyle: "italic" };
      },
      cellRenderer: p => {
        if (p.node.rowPinned) return "";
        return p.data.limitPrice ? `₹${fmtD(p.data.limitPrice)}` : "market";
      },
      tooltipValueGetter: () => "Optional: set a limit price. Leave blank for MARKET order. Required for ASM/surveillance stocks like BHEL.",
    },
    {
      headerName: "LTP", field: "ltp", minWidth: 80,
      valueFormatter: p => p.node.rowPinned ? "" : (p.value != null ? `₹${fmtD(p.value)}` : "—"),
    },
    {
      headerName: "Avg Price", field: "avgPrice", minWidth: 95,
      valueFormatter: p => p.node.rowPinned ? "" : (p.value != null ? `₹${fmtD(p.value)}` : "—"),
    },
    {
      headerName: "Current P&L", field: "pnl", minWidth: 120,
      cellStyle: p => ({ color: pnlColor(p.value), fontWeight: 600 }),
      valueFormatter: p => pnlFmt(p.value),
    },
    {
      headerName: "Potential P&L", field: "potential_pnl", minWidth: 125,
      cellStyle: p => ({ fontWeight: p.node.rowPinned ? 700 : undefined, color: pnlColor(p.value) }),
      valueFormatter: p => (p.value != null && p.value !== 0) ? pnlFmt(p.value) : "—",
    },
    {
      headerName: "Net Δ", field: "netDelta", minWidth: 85,
      cellStyle: p => {
        if (p.node.rowPinned) return { fontWeight: 700 };
        const v = p.value;
        return { color: v == null ? "#bbb" : v >= 0 ? "#28a745" : "#dc3545" };
      },
      valueFormatter: p => {
        if (p.value == null) return "—";
        return `${p.value >= 0 ? "+" : ""}${fmtD(p.value, 2)}`;
      },
    },
    {
      headerName: "IV %", field: "iv", minWidth: 70,
      valueFormatter: p => p.node.rowPinned ? "" : (p.value ? `${(p.value * 100).toFixed(1)}%` : "—"),
    },
    {
      headerName: "Delta", field: "delta", minWidth: 70,
      valueFormatter: p => p.node.rowPinned ? "" : (p.value != null ? fmtD(p.value) : "—"),
    },
    {
      headerName: "Theta", field: "theta", minWidth: 70,
      valueFormatter: p => p.node.rowPinned ? "" : (p.value != null ? fmtD(p.value) : "—"),
    },
  ], [availableExpiries]);

  const defaultColDef = useMemo(() => ({
    flex: 1, resizable: true, sortable: false, filter: false,
  }), []);

  /* ── add option: toggle inline form ── */
  const addOptionRow = useCallback(() => {
    setShowOptionForm(v => !v);
  }, []);

  const handleStrikeSelect = useCallback((strikeStr) => {
    const contract = optionForm.contracts.find(c => String(c.strike) === strikeStr) || null;
    setOptionForm(f => ({ ...f, selectedStrike: strikeStr, selectedContract: contract }));
  }, [optionForm.contracts]);

  const commitOptionRow = useCallback(() => {
    const c = optionForm.selectedContract;
    if (!c) return;
    const lotsize = c.lotsize || 1;
    const lots = Number(optionForm.lots) || 1;
    setRowData(prev => {
      const rows = [...prev, {
        id: `NEW-OPT-${Date.now()}`,
        instrument: "OPTION",
        optionType: optionForm.type,
        expiry: optionForm.expiry,
        strike: c.strike,
        side: optionForm.side || null,
        qty: lots * lotsize,
        lots,
        _lotsize: lotsize,
        ltp: c.ltp,
        avgPrice: c.ltp,
        pnl: 0,
        potential_pnl: 0,
        netDelta: parseFloat((c.delta * (optionForm.side === "BUY" ? 1 : -1) * lots).toFixed(2)),
        iv: c.abs_delta != null ? null : null,
        delta: c.delta,
        theta: c.theta,
        _isNew: true,
      }];
      rows.sort((a, b) => sortKey(a) - sortKey(b));
      return rows;
    });
    setShowOptionForm(false);
    setOptionForm(f => ({ ...f, selectedStrike: "", selectedContract: null }));
  }, [optionForm]);

  const addStockRow = useCallback(() => {
    setShowStockForm(v => !v);
  }, []);

  const commitStockRow = useCallback(() => {
    const qty = Number(stockForm.qty) || 0;
    if (!qty || !stockForm.side) return;
    const lotsize = stockInfo?.lot_size || 1;
    setRowData(prev => {
      const rows = [...prev, {
        id: `NEW-STOCK-${Date.now()}`,
        instrument: "STOCK", optionType: null,
        side: stockForm.side,
        qty,
        lots: parseFloat((qty / lotsize).toFixed(2)),
        _lotsize: lotsize,
        ltp: stockInfo?.ltp || 0,
        avgPrice: stockInfo?.avg_price || 0,
        limitPrice: stockForm.limitPrice ? Number(stockForm.limitPrice) : null,
        pnl: 0, potential_pnl: 0, netDelta: 0, _isNew: true,
      }];
      rows.sort((a, b) => sortKey(a) - sortKey(b));
      return rows;
    });
    setShowStockForm(false);
    setStockForm({ qty: 1, side: "BUY", limitPrice: "" });
  }, [stockForm, stockInfo]);

  const addFutureRow = useCallback(() => {
    setShowFutureForm(v => !v);
  }, []);

  const handleFutureContractSelect = useCallback((symbol) => {
    const contract = futureForm.contracts.find(c => c.symbol === symbol) || null;
    setFutureForm(f => ({ ...f, selectedSymbol: symbol, selectedContract: contract }));
  }, [futureForm.contracts]);

  const commitFutureRow = useCallback(() => {
    const c = futureForm.selectedContract;
    if (!c) return;
    const lotsize = c.lotsize || 1;
    const lots = Number(futureForm.lots) || 1;
    setRowData(prev => {
      const rows = [...prev, {
        id: `NEW-FUT-${Date.now()}`,
        instrument: "FUTURE", optionType: null,
        expiry: c.expiry, strike: null,
        side: futureForm.side || null,
        qty: lots * lotsize,
        lots,
        _lotsize: lotsize,
        ltp: c.ltp,
        avgPrice: c.ltp,
        pnl: 0, potential_pnl: 0, netDelta: lots,
        iv: null, delta: null, theta: null,
        _isNew: true,
      }];
      rows.sort((a, b) => sortKey(a) - sortKey(b));
      return rows;
    });
    setShowFutureForm(false);
    setFutureForm(f => ({ ...f, selectedSymbol: "", selectedContract: null }));
  }, [futureForm]);

  const commitAlert = useCallback(async () => {
    if (!alertForm.target_price) return;
    setAlertSaving(true);
    setAlertMsg(null);
    try {
      await axios.post("/alerts/price", {
        username: selectedAccount,
        symbolname: stockname.toUpperCase(),
        direction: alertForm.direction,
        target_price: alertForm.target_price,
        note: alertForm.note,
      });
      setAlertMsg({ ok: true, text: "Alert created." });
      setAlertForm({ direction: "above", target_price: "", note: "" });
      setTimeout(() => { setAlertMsg(null); setShowAlertForm(false); }, 1800);
    } catch (err) {
      setAlertMsg({ ok: false, text: err.response?.data?.error || "Failed to create alert." });
    } finally {
      setAlertSaving(false);
    }
  }, [alertForm, selectedAccount, stockname]);

  /* ── reset: clear all action selections ── */
  const resetChanges = useCallback(() => {
    setRowData(prev => prev.map(r => ({ ...r, side: null })));
    setOrderResults(null);
    setSubmitError(null);
  }, []);

  /* ── submit: only rows with side selected ── */
  const submitTrades = useCallback(async () => {
    const toSubmit = rowData.filter(r => r.side);
    if (!toSubmit.length) {
      alert("No rows selected. Click the Action cell on each row and choose BUY or SELL first.");
      return;
    }
    const orders = toSubmit.map(r => ({
      username: selectedAccount, underlying_stock: stockname,
      transactiontype: r.side,
      lots: r.instrument === "STOCK" ? Number(r.qty || 0) : Number(r.lots || 0),
      pe_ce: r.instrument === "OPTION" ? r.optionType : r.instrument === "FUTURE" ? "FUT" : "ST",
      strikeprice: r.instrument === "OPTION" ? Number(r.strike) : 0,
      expiry: (r.instrument === "OPTION" || r.instrument === "FUTURE") ? r.expiry : "",
      buyprice:  (r.side === "BUY"  && r.limitPrice) ? r.limitPrice : 0,
      sellprice: (r.side === "SELL" && r.limitPrice) ? r.limitPrice : 0,
    }));
    setSubmitError(null);
    try {
      const res = await axios.post("/place_orders_bulk", { orders }, { withCredentials: true });
      setOrderResults(res.data.orders);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Order submission failed";
      console.error("Submit error:", msg);
      setSubmitError(msg);
    }
  }, [rowData, selectedAccount, stockname]);

  const onCellValueChanged = useCallback(() => {
    setRowData(prev => [...prev]);
  }, []);

  const selectedCount = rowData.filter(r => r.side).length;

  /* ── assign strategy ── */
  const handleAssignStrategy = useCallback(async () => {
    const shortCE = options.find(o => o.optiontype === "CE" && o.netqty < 0);
    if (!shortCE) {
      alert("No active short call found for this stock. Sell a CE first before assigning a strategy.");
      return;
    }
    setShowAssignModal(true);
    setAssignMsg(null);
  }, [options]);

  const submitAssignStrategy = useCallback(async () => {
    if (!assignExpiry) {
      setAssignMsg({ ok: false, text: "Please select an expiry." });
      return;
    }
    setAssignSaving(true);
    setAssignMsg(null);
    try {
      await axios.post("/strategy/assign", {
        username: selectedAccount,
        symbolname: stockname,
        expiry: assignExpiry,
      });
      setAssignMsg({ ok: true, text: "Strategy assigned successfully." });
      setStrategyInfo({ exists: true, status: "INACTIVE" });
      setTimeout(() => { setShowAssignModal(false); setAssignMsg(null); }, 1800);
    } catch (err) {
      setAssignMsg({ ok: false, text: err.response?.data?.error || "Failed to assign strategy." });
    } finally {
      setAssignSaving(false);
    }
  }, [assignExpiry, selectedAccount, stockname]);

  /* ── render ── */
  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spinner color="primary" /></div>;

  return (
    <>
      <PageTitleAlt2
        heading={`${stockname} — Trade Detail`}
        subheading="Stock and option positions"
        icon="pe-7s-note2 icon-gradient bg-sunny-morning"
      />
      <div style={{ padding: "0 25px 30px" }}>

        {/* Summary cards */}
        {totals && (
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Stock P&L"         value={pnlFmt(totals.stock_pnl)}             color={pnlColor(totals.stock_pnl)} /></div>
            {futures.length > 0 && (
              <div style={{ flex: "1 1 150px" }}><SummaryCard label="Futures P&L"     value={pnlFmt(totals.future_pnl)}            color={pnlColor(totals.future_pnl)} /></div>
            )}
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Option P&L"        value={pnlFmt(totals.option_pnl)}            color={pnlColor(totals.option_pnl)} /></div>
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Combined P&L"      value={pnlFmt(totals.combined_pnl)}          color={pnlColor(totals.combined_pnl)} /></div>
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Option Realized"   value={pnlFmt(totals.booked_option_profit)}  color={pnlColor(totals.booked_option_profit)} /></div>
            {totals.stock_realized_profit !== 0 && (
              <div style={{ flex: "1 1 150px" }}><SummaryCard label="Stock Realized"  value={pnlFmt(totals.stock_realized_profit)} color={pnlColor(totals.stock_realized_profit)} /></div>
            )}
            {totals.futures_realized_profit !== 0 && (
              <div style={{ flex: "1 1 150px" }}><SummaryCard label="Futures Realized" value={pnlFmt(totals.futures_realized_profit)} color={pnlColor(totals.futures_realized_profit)} /></div>
            )}
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Total Realized"    value={pnlFmt(totals.total_booked)}          color={pnlColor(totals.total_booked)} /></div>
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Today's Booked Profit" value={pnlFmt(totals.today_booked_profit)} color={pnlColor(totals.today_booked_profit)} /></div>
            <div style={{ flex: "1 1 150px" }}><SummaryCard label="Total Potential"   value={pnlFmt((totals.total_booked || 0) + (totals.potential_pnl || 0))} color={pnlColor((totals.total_booked || 0) + (totals.potential_pnl || 0))} /></div>
          </div>
        )}

        {/* Stock info strip */}
        {stockInfo && (
          <Card className="mb-3" style={{ borderRadius: 10, border: "1px solid #eee" }}>
            <CardBody style={{ padding: "12px 20px" }}>
              <div style={{ display: "flex", gap: 28, flexWrap: "wrap", fontSize: 13, alignItems: "center" }}>
                <span><span style={{ color: "#888" }}>Qty </span><b>{stockInfo.quantity.toLocaleString()}</b></span>
                {stockInfo.lot_size > 1 && <span><span style={{ color: "#888" }}>Lot Size </span><b>{stockInfo.lot_size.toLocaleString()}</b></span>}
                <span><span style={{ color: "#888" }}>Avg </span><b>₹{fmtD(stockInfo.avg_price)}</b></span>
                <span><span style={{ color: "#888" }}>LTP </span><b>₹{fmtD(stockInfo.ltp)}</b></span>
                <span><span style={{ color: "#888" }}>Invested </span><b>₹{fmt(stockInfo.invested)}</b></span>
                <span>
                  <span style={{ color: "#888" }}>Stock P&L </span>
                  <b style={{ color: pnlColor(stockInfo.stock_pnl) }}>{pnlFmt(stockInfo.stock_pnl)}</b>
                </span>
                <Badge color={options.length > 0 ? "info" : "light"} pill style={{ fontSize: 12 }}>
                  {options.length} option{options.length !== 1 ? "s" : ""}
                </Badge>
                {futures.length > 0 && (
                  <Badge color="success" pill style={{ fontSize: 12 }}>
                    {futures.length} future{futures.length !== 1 ? "s" : ""}
                  </Badge>
                )}
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  style={{ fontSize: 12 }}
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? <><Spinner size="sm" /> Refreshing…</> : "⟳ Refresh"}
                </Button>
                {strategyInfo?.exists ? (
                  <Button
                    size="sm"
                    color="info"
                    outline
                    style={{ marginLeft: "auto", fontSize: 12 }}
                    onClick={() => window.location.href = `/strategy/manage?symbol=${stockname}`}
                  >
                    Manage Strategy
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="success"
                    outline
                    style={{ marginLeft: "auto", fontSize: 12 }}
                    onClick={handleAssignStrategy}
                  >
                    Assign Strategy
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {!stockInfo && !loading && (
          <div style={{ padding: "12px 20px", background: "#fafafa", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ color: "#888", fontSize: 13 }}>No stock position for <b>{stockname}</b>.</span>
            <Button size="sm" color="secondary" outline style={{ fontSize: 12 }} onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <><Spinner size="sm" /> Refreshing…</> : "⟳ Refresh"}
            </Button>
          </div>
        )}

        {/* Positions grid */}
        <Card style={{ borderRadius: 12, border: "1px solid #eee" }}>
          <CardBody>
            {/* Add buttons above grid */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <Button size="sm" color={showOptionForm ? "secondary" : "success"} onClick={addOptionRow}>
                {showOptionForm ? "✕ Cancel" : "+ Add Option"}
              </Button>
              <Button size="sm" color={showStockForm ? "secondary" : "primary"} onClick={addStockRow}>
                {showStockForm ? "✕ Cancel" : "+ Add Stock"}
              </Button>
              <Button size="sm" color={showFutureForm ? "secondary" : "info"} onClick={addFutureRow}>
                {showFutureForm ? "✕ Cancel" : "+ Add Future"}
              </Button>
              <Button size="sm" color={showAlertForm ? "secondary" : "warning"}
                style={{ color: showAlertForm ? undefined : "#333" }}
                onClick={() => { setShowAlertForm(v => !v); setAlertMsg(null); }}>
                {showAlertForm ? "✕ Cancel" : "+ Add Alert"}
              </Button>
            </div>

            {/* Inline option builder form */}
            {showOptionForm && (
              <div style={{ background: "#f8f9ff", border: "1px solid #c5cbe8", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "#333" }}>New Option Row</div>
                {!stockInfo?.ltp && (
                  <div style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>⚠ No stock LTP available — cannot fetch strikes.</div>
                )}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>

                  {/* CE / PE toggle */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Type</div>
                    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
                      {["CE", "PE"].map(t => (
                        <button key={t} onClick={() => setOptionForm(f => ({ ...f, type: t, selectedStrike: "", selectedContract: null }))}
                          style={{ padding: "4px 14px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                            background: optionForm.type === t ? (t === "CE" ? "#17a2b8" : "#fd7e14") : "#fff",
                            color: optionForm.type === t ? "#fff" : "#555" }}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Expiry */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Expiry</div>
                    <select value={optionForm.expiry} onChange={e => setOptionForm(f => ({ ...f, expiry: e.target.value, selectedStrike: "", selectedContract: null }))}
                      style={{ padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }}>
                      {availableExpiries.map(exp => (
                        <option key={exp} value={exp}>
                          {new Date(exp + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Strike */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                      Strike {optionForm.loadingContracts && <span style={{ color: "#888" }}>loading…</span>}
                    </div>
                    <select value={optionForm.selectedStrike} onChange={e => handleStrikeSelect(e.target.value)}
                      disabled={optionForm.loadingContracts || optionForm.contracts.length === 0}
                      style={{ padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, minWidth: 140 }}>
                      <option value="">— select strike —</option>
                      {optionForm.contracts.map(c => (
                        <option key={c.strike} value={String(c.strike)}>
                          ₹{c.strike} (Δ {c.delta > 0 ? "+" : ""}{c.delta})
                        </option>
                      ))}
                    </select>
                    {optionForm.contractsError && <div style={{ color: "#dc3545", fontSize: 11, marginTop: 2 }}>{optionForm.contractsError}</div>}
                  </div>

                  {/* Auto-filled greeks (read-only) */}
                  {optionForm.selectedContract && (() => {
                    const c = optionForm.selectedContract;
                    return (
                      <>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>LTP</div>
                          <div style={{ fontWeight: 700 }}>₹{c.ltp}</div>
                        </div>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>Delta</div>
                          <div style={{ fontWeight: 700 }}>{c.delta}</div>
                        </div>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>Theta</div>
                          <div style={{ fontWeight: 700 }}>{c.theta}</div>
                        </div>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>OI</div>
                          <div style={{ fontWeight: 700 }}>{c.oi?.toLocaleString()}</div>
                        </div>
                      </>
                    );
                  })()}

                  {/* Lots */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Lots</div>
                    <input type="number" min="1" value={optionForm.lots}
                      onChange={e => setOptionForm(f => ({ ...f, lots: e.target.value }))}
                      style={{ width: 60, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </div>

                  {/* BUY / SELL */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Action</div>
                    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
                      {["BUY", "SELL"].map(s => (
                        <button key={s} onClick={() => setOptionForm(f => ({ ...f, side: s }))}
                          style={{ padding: "4px 14px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                            background: optionForm.side === s ? (s === "BUY" ? "#28a745" : "#dc3545") : "#fff",
                            color: optionForm.side === s ? "#fff" : "#555" }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Add button */}
                  <button onClick={commitOptionRow} disabled={!optionForm.selectedContract}
                    style={{ padding: "6px 18px", background: optionForm.selectedContract ? "#0055aa" : "#ccc",
                      color: "#fff", border: "none", borderRadius: 4, cursor: optionForm.selectedContract ? "pointer" : "not-allowed",
                      fontWeight: 600, fontSize: 13, alignSelf: "flex-end", marginBottom: 1 }}>
                    Add to Grid
                  </button>
                </div>
              </div>
            )}

            {/* Inline stock form */}
            {showStockForm && (
              <div style={{ background: "#f0f8ff", border: "1px solid #b8d4f0", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "#333" }}>New Stock Row</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>

                  {/* Symbol (read-only) */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Symbol</div>
                    <div style={{ padding: "5px 12px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 13, fontWeight: 700, color: "#333" }}>
                      {stockname.toUpperCase()}
                    </div>
                  </div>

                  {/* LTP hint */}
                  {stockInfo?.ltp && (
                    <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                      <div style={{ color: "#888", fontSize: 10 }}>LTP</div>
                      <div style={{ fontWeight: 700 }}>₹{fmtD(stockInfo.ltp)}</div>
                    </div>
                  )}

                  {/* Qty */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Qty (shares)</div>
                    <input type="number" min="1" value={stockForm.qty}
                      onChange={e => setStockForm(f => ({ ...f, qty: e.target.value }))}
                      style={{ width: 80, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </div>

                  {/* BUY / SELL */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Action</div>
                    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
                      {["BUY", "SELL"].map(s => (
                        <button key={s} onClick={() => setStockForm(f => ({ ...f, side: s }))}
                          style={{ padding: "4px 14px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                            background: stockForm.side === s ? (s === "BUY" ? "#28a745" : "#dc3545") : "#fff",
                            color: stockForm.side === s ? "#fff" : "#555" }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Limit price */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Limit ₹ <span style={{ color: "#aaa" }}>(optional)</span></div>
                    <input type="number" min="0" step="0.05" placeholder="market"
                      value={stockForm.limitPrice}
                      onChange={e => setStockForm(f => ({ ...f, limitPrice: e.target.value }))}
                      style={{ width: 90, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </div>

                  <button onClick={commitStockRow} disabled={!stockForm.qty || !stockForm.side}
                    style={{ padding: "6px 18px",
                      background: stockForm.qty && stockForm.side ? "#0055aa" : "#ccc",
                      color: "#fff", border: "none", borderRadius: 4,
                      cursor: stockForm.qty && stockForm.side ? "pointer" : "not-allowed",
                      fontWeight: 600, fontSize: 13, alignSelf: "flex-end", marginBottom: 1 }}>
                    Add to Grid
                  </button>
                </div>
              </div>
            )}

            {/* Inline future builder form */}
            {showFutureForm && (
              <div style={{ background: "#f0fbf9", border: "1px solid #a8dcd2", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "#333" }}>New Future Row</div>
                {futureForm.contractsError && (
                  <div style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>⚠ {futureForm.contractsError}</div>
                )}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>

                  {/* Month / contract */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                      Month {futureForm.loadingContracts && <span style={{ color: "#888" }}>loading…</span>}
                    </div>
                    <select value={futureForm.selectedSymbol} onChange={e => handleFutureContractSelect(e.target.value)}
                      disabled={futureForm.loadingContracts || futureForm.contracts.length === 0}
                      style={{ padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, minWidth: 220 }}>
                      <option value="">— select month —</option>
                      {futureForm.contracts.map(c => (
                        <option key={c.symbol} value={c.symbol}>
                          {c.label} — {c.symbol} ({new Date(c.expiry + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Auto-filled bid/ask/ltp (read-only) */}
                  {futureForm.selectedContract && (() => {
                    const c = futureForm.selectedContract;
                    return (
                      <>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>LTP</div>
                          <div style={{ fontWeight: 700 }}>{c.ltp != null ? `₹${c.ltp}` : "—"}</div>
                        </div>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>Bid</div>
                          <div style={{ fontWeight: 700, color: "#28a745" }}>{c.bid != null ? `₹${c.bid}` : "—"}</div>
                        </div>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>Ask</div>
                          <div style={{ fontWeight: 700, color: "#dc3545" }}>{c.ask != null ? `₹${c.ask}` : "—"}</div>
                        </div>
                        <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                          <div style={{ color: "#888", fontSize: 10 }}>Lot Size</div>
                          <div style={{ fontWeight: 700 }}>{c.lotsize}</div>
                        </div>
                      </>
                    );
                  })()}

                  {/* Lots */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Lots</div>
                    <input type="number" min="1" value={futureForm.lots}
                      onChange={e => setFutureForm(f => ({ ...f, lots: e.target.value }))}
                      style={{ width: 60, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </div>

                  {/* BUY / SELL */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Action</div>
                    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
                      {["BUY", "SELL"].map(s => (
                        <button key={s} onClick={() => setFutureForm(f => ({ ...f, side: s }))}
                          style={{ padding: "4px 14px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                            background: futureForm.side === s ? (s === "BUY" ? "#28a745" : "#dc3545") : "#fff",
                            color: futureForm.side === s ? "#fff" : "#555" }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Add button */}
                  <button onClick={commitFutureRow} disabled={!futureForm.selectedContract}
                    style={{ padding: "6px 18px", background: futureForm.selectedContract ? "#0055aa" : "#ccc",
                      color: "#fff", border: "none", borderRadius: 4, cursor: futureForm.selectedContract ? "pointer" : "not-allowed",
                      fontWeight: 600, fontSize: 13, alignSelf: "flex-end", marginBottom: 1 }}>
                    Add to Grid
                  </button>
                </div>
              </div>
            )}

            {/* Inline alert form */}
            {showAlertForm && (
              <div style={{ background: "#fffbf0", border: "1px solid #e8d88a", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "#333" }}>New Price Alert</div>
                {alertMsg && (
                  <div style={{ marginBottom: 8, padding: "6px 12px", borderRadius: 4, fontSize: 12, fontWeight: 600,
                    background: alertMsg.ok ? "#d4edda" : "#f8d7da",
                    color: alertMsg.ok ? "#155724" : "#721c24" }}>
                    {alertMsg.text}
                  </div>
                )}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>

                  {/* Symbol (read-only) */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Symbol</div>
                    <div style={{ padding: "5px 12px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 13, fontWeight: 700, color: "#333" }}>
                      {stockname.toUpperCase()}
                    </div>
                  </div>

                  {/* LTP hint */}
                  {stockInfo?.ltp && (
                    <div style={{ padding: "4px 10px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12, minWidth: 70 }}>
                      <div style={{ color: "#888", fontSize: 10 }}>Current LTP</div>
                      <div style={{ fontWeight: 700 }}>₹{fmtD(stockInfo.ltp)}</div>
                    </div>
                  )}

                  {/* Direction */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Condition</div>
                    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
                      {[["above", "▲ Above"], ["below", "▼ Below"]].map(([val, label]) => (
                        <button key={val} onClick={() => setAlertForm(f => ({ ...f, direction: val }))}
                          style={{ padding: "4px 12px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                            background: alertForm.direction === val ? (val === "above" ? "#856404" : "#c0392b") : "#fff",
                            color: alertForm.direction === val ? "#fff" : "#555" }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Target price */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Target Price ₹</div>
                    <input type="number" min="0" step="0.05" placeholder="0.00"
                      value={alertForm.target_price}
                      onChange={e => setAlertForm(f => ({ ...f, target_price: e.target.value }))}
                      style={{ width: 110, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </div>

                  {/* Note */}
                  <div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>Note <span style={{ color: "#aaa" }}>(optional)</span></div>
                    <input type="text" placeholder="e.g. Breakout level"
                      value={alertForm.note}
                      onChange={e => setAlertForm(f => ({ ...f, note: e.target.value }))}
                      style={{ width: 160, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  </div>

                  <button onClick={commitAlert} disabled={alertSaving || !alertForm.target_price}
                    style={{ padding: "6px 18px",
                      background: alertForm.target_price && !alertSaving ? "#856404" : "#ccc",
                      color: "#fff", border: "none", borderRadius: 4,
                      cursor: alertForm.target_price && !alertSaving ? "pointer" : "not-allowed",
                      fontWeight: 600, fontSize: 13, alignSelf: "flex-end", marginBottom: 1 }}>
                    {alertSaving ? "Saving…" : "Create Alert"}
                  </button>
                </div>
              </div>
            )}

            <div className="ag-theme-alpine" style={{ width: "100%" }}>
              <AgGridReact
                theme="legacy"
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                domLayout="autoHeight"
                rowHeight={40}
                headerHeight={40}
                pinnedBottomRowData={pinnedRow}
                onCellValueChanged={onCellValueChanged}
                singleClickEdit={true}
                stopEditingWhenCellsLoseFocus={true}
                getRowStyle={p => {
                  if (p.node.rowPinned) return { fontWeight: "bold", background: "#f2f2f2" };
                  if (p.data && p.data._isNew) return { background: "#fff8e1" };
                  return p.node.rowIndex % 2 === 0 ? { background: "#fff" } : { background: "#fafafa" };
                }}
              />
            </div>

            {rowData.length === 0 && (
              <div style={{ textAlign: "center", padding: 20, color: "#aaa", fontSize: 13 }}>
                No positions. Use "+ Add Option", "+ Add Stock", or "+ Add Future" to add rows.
              </div>
            )}

            {/* Action buttons below grid */}
            <div style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
              <Button color="secondary" onClick={resetChanges}>Reset Buy/Sell</Button>
              <Button color="danger" onClick={submitTrades}>
                Submit Trades {selectedCount > 0 ? `(${selectedCount})` : ""}
              </Button>
              {selectedCount > 0 && (
                <span style={{ fontSize: 12, color: "#666" }}>
                  {selectedCount} row{selectedCount !== 1 ? "s" : ""} queued
                </span>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Submit error */}
        {submitError && (
          <Card className="mt-3" style={{ borderRadius: 10, border: "1px solid #dc3545" }}>
            <CardBody style={{ color: "#dc3545", fontWeight: 600 }}>
              Order submission failed: {submitError}
            </CardBody>
          </Card>
        )}

        {/* Order results */}
        {orderResults && (
          <Card className="mt-3" style={{ borderRadius: 10 }}>
            <CardHeader style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span>Order Status</span>
              {orderResults.some(o => o.status === "FAILED") && (
                <Badge color="danger" pill>{orderResults.filter(o => o.status === "FAILED").length} failed</Badge>
              )}
              {orderResults.every(o => o.status !== "FAILED") && (
                <Badge color="success" pill>All placed</Badge>
              )}
            </CardHeader>
            <CardBody style={{ padding: 0 }}>
              <Table size="sm" bordered className="mb-0">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Qty</th>
                    <th>Status</th>
                    <th>Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {orderResults.map((o, i) => (
                    <React.Fragment key={i}>
                      <tr style={{ background: o.status === "FAILED" ? "#fff0f0" : "#fff" }}>
                        <td style={{ fontWeight: 600 }}>{o.tradingsymbol}</td>
                        <td style={{ color: o.transactiontype === "BUY" ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                          {o.transactiontype}
                        </td>
                        <td>{o.quantity || "—"}</td>
                        <td>
                          <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontWeight: 700,
                            fontSize: 12,
                            background: o.status === "FAILED" ? "#dc354520" : o.status === "PLACED" ? "#0055aa18" : "#28a74520",
                            color: o.status === "FAILED" ? "#dc3545" : o.status === "PLACED" ? "#0055aa" : "#28a745",
                          }}>
                            {o.status}
                          </span>
                        </td>
                        <td style={{ fontFamily: "monospace", fontSize: 12 }}>{o.orderid || "—"}</td>
                      </tr>
                      {o.status === "FAILED" && o.error && (
                        <tr style={{ background: "#fff0f0" }}>
                          <td colSpan={5} style={{ padding: "6px 12px" }}>
                            <div style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              background: "#dc354510", border: "1px solid #dc354540",
                              borderRadius: 6, padding: "8px 12px",
                            }}>
                              <span style={{ fontSize: 16, lineHeight: 1.2 }}>⚠️</span>
                              <div>
                                <div style={{ fontWeight: 700, color: "#dc3545", fontSize: 12, marginBottom: 2 }}>
                                  Reason for failure
                                </div>
                                <div style={{ color: "#7a0000", fontSize: 13 }}>{o.error}</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        )}
      </div>

      <style>{`
        .ag-theme-alpine { font-family: "Segoe UI", Roboto, sans-serif; font-size: 13px; }
        .ag-theme-alpine .ag-row-hover { background: #eef4ff !important; }
      `}</style>

      {/* Assign Strategy Modal */}
      <Modal isOpen={showAssignModal} toggle={() => { setShowAssignModal(false); setAssignMsg(null); }}>
        <ModalHeader toggle={() => { setShowAssignModal(false); setAssignMsg(null); }}>
          Assign Strategy — {stockname}
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
            This creates a Pyramid Hedging strategy record linked to your existing short call.
            You can build the full ladder later from the Build Strategy page.
          </p>
          <FormGroup>
            <Label style={{ fontWeight: 600 }}>Expiry</Label>
            <Input
              type="select"
              value={assignExpiry}
              onChange={e => setAssignExpiry(e.target.value)}
            >
              <option value="">— Select expiry —</option>
              {availableExpiries.map(exp => (
                <option key={exp} value={exp}>
                  {new Date(exp + "T00:00:00").toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric"
                  })}
                </option>
              ))}
            </Input>
          </FormGroup>
          {assignMsg && (
            <div style={{
              padding: "8px 12px", borderRadius: 6, fontSize: 13,
              background: assignMsg.ok ? "#d4edda" : "#f8d7da",
              color: assignMsg.ok ? "#155724" : "#721c24",
            }}>
              {assignMsg.text}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={submitAssignStrategy} disabled={assignSaving}>
            {assignSaving ? <Spinner size="sm" /> : "Assign Strategy"}
          </Button>
          <Button color="secondary" outline onClick={() => { setShowAssignModal(false); setAssignMsg(null); }}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TradeDetail;
